// Survey lifecycle helpers (WP3).
//
// The survey lifecycle state is the server field `surveys.status`, one of
// ready | deployed | staged | sent (sam-dbam migration 024). The console
// renders its panels and Staging Area FROM this field — never from
// localStorage. These are the pure helpers the panels and App use to read and
// present that state.

export const LIFECYCLE_READY = 'ready';
export const LIFECYCLE_DEPLOYED = 'deployed';
export const LIFECYCLE_STAGED = 'staged';
export const LIFECYCLE_SENT = 'sent';

const VALID_LIFECYCLE = new Set([
    LIFECYCLE_READY,
    LIFECYCLE_DEPLOYED,
    LIFECYCLE_STAGED,
    LIFECYCLE_SENT,
]);

/**
 * Normalize a survey's lifecycle status to one of the four server states.
 *
 * Post-migration, `surveys.status` already holds one of the four values and is
 * returned verbatim. For resilience against a legacy value that predates the
 * migration (draft/active/closed/archived or empty), we derive a best-effort
 * value from the presence of an open deployment: an open deployment implies
 * `deployed`, otherwise `ready`. sam-dbam remains the single source of truth;
 * this only keeps the console coherent during/after the rollout.
 *
 * @param {object} survey       - Survey record (carries `status`, `survey_id`).
 * @param {Array}  deployments  - All deployments (used only for legacy fallback).
 * @returns {string} one of ready|deployed|staged|sent
 */
export function normalizeLifecycleStatus(survey, deployments = []) {
    const raw = String(survey?.status || '').toLowerCase();
    if (VALID_LIFECYCLE.has(raw)) return raw;
    const surveyId = survey?.survey_id || survey?.id;
    const hasOpen = (deployments || []).some(
        d => d.survey_id === surveyId
            && (d.is_open === true || d.status === 'open' || d.status === 'active')
    );
    return hasOpen ? LIFECYCLE_DEPLOYED : LIFECYCLE_READY;
}

/**
 * Build the completion-checked undeploy confirmation message from the server's
 * counts (the `counts` block on the /undeploy `requires_confirmation` response).
 *
 * Named deployments phrase the shortfall with x = sent, y = responding. Anonymous
 * link deployments have no fixed x, so they are phrased with started/completed
 * session counts instead (per the WP3 spec, x is undefined there).
 *
 * @param {object} counts - {sent_count, responding, started_count,
 *                           completed_count, is_anonymous}
 * @returns {string}
 */
export function formatUndeployConfirmation(counts = {}) {
    const sent = Number(counts.sent_count) || 0;
    const responding = Number(counts.responding) || 0;
    const started = Number(counts.started_count) || 0;
    const completed = Number(counts.completed_count) || 0;

    if (counts.is_anonymous) {
        return `This is an anonymous link survey with no fixed recipient list. `
            + `${completed} of ${started} started session${started === 1 ? '' : 's'} `
            + `${completed === 1 ? 'has' : 'have'} completed. `
            + `Are you certain no further responses are likely before you undeploy?`;
    }

    const remaining = Math.max(sent - responding, 0);
    return `${sent} sent, ${responding} responded — are you certain the remaining `
        + `${remaining} recipient${remaining === 1 ? '' : 's'} are not likely to respond?`;
}

/**
 * Reconciles a list of linked surveys against a set of staged deployment IDs.
 *
 * @param {Array<object>} linkedSurveys An array of survey objects, each with a `deploymentId` property.
 * @param {Set<string>} stagedDeploymentIdSet A Set of deployment ID strings that are currently staged.
 * @returns {{kept: Array<object>, dropped: Array<object>}} An object containing two arrays:
 *   - `kept`: Surveys whose `deploymentId` is present in the `stagedDeploymentIdSet`.
 *   - `dropped`: Surveys whose `deploymentId` is not present in the `stagedDeploymentIdSet`.
 */
export const reconcileStagedSurveys = (linkedSurveys, stagedDeploymentIdSet) => {
    // Handle null/undefined inputs gracefully.
    const surveysToProcess = linkedSurveys || [];
    const stagedIds = stagedDeploymentIdSet || new Set();

    const kept = [];
    const dropped = [];

    for (const survey of surveysToProcess) {
        if (survey && typeof survey.deploymentId === 'string') { // Ensure deploymentId exists and is a string
            if (stagedIds.has(survey.deploymentId)) {
                kept.push(survey);
            } else {
                dropped.push(survey);
            }
        } else {
            // If survey or its deploymentId are invalid, treat as dropped to be safe.
            dropped.push(survey);
        }
    }

    return { kept, dropped };
};
